import { AxiosError, isAxiosError, isCancel } from "axios";

/**
 * Codes axios uses when a request ends without an HTTP answer (reported by the
 * browser as status 0): offline or blocked (ERR_NETWORK), aborted (ERR_CANCELED)
 * and timed out (ECONNABORTED / ETIMEDOUT).
 */
const NO_RESPONSE_CODES = new Set<string>([
  AxiosError.ERR_NETWORK,
  AxiosError.ERR_CANCELED,
  AxiosError.ECONNABORTED,
  AxiosError.ETIMEDOUT,
]);

/**
 * True when a request failed without ever getting an answer from the backend.
 * There is nothing for us to fix in these (a flaky connection, a navigation that
 * aborted the XHR, an ad blocker), so they should neither crash the UI nor be
 * reported to Sentry.
 *
 * Unlike `isTransientNetworkError`, which decides whether a retry is worth it
 * and so excludes cancellations, this includes them.
 */
export const isNoResponseError = (error: unknown): boolean => {
  if (isCancel(error)) return true;

  if (!isAxiosError(error) || error.response) return false;

  return error.code !== undefined && NO_RESPONSE_CODES.has(error.code);
};

interface ISentryEventLike {
  exception?: {
    values?: { value?: string; mechanism?: { synthetic?: boolean } }[];
  };
}

/**
 * True for Sentry events that come from a request that got no response, so a
 * `beforeSend` can drop them wherever they were captured. Two shapes:
 *
 * - the AxiosError itself, e.g. an unhandled "Network Error" rejection;
 * - `captureException(undefined)`. The customer and partner response interceptor
 *   rejects with `error.response`, which is `undefined` when there was no
 *   response, so every `onError` that forwards its argument ends up capturing
 *   `undefined`. Sentry reports that as a stackless "Error: undefined" with a
 *   synthetic mechanism and no `originalException`. It carries nothing to act on.
 */
export const isNoResponseSentryEvent = (
  event: ISentryEventLike,
  hint: { originalException?: unknown } | undefined,
): boolean => {
  const originalException = hint?.originalException;

  if (isNoResponseError(originalException)) return true;

  const [exception] = event.exception?.values ?? [];

  return (
    originalException === undefined &&
    exception?.value === "undefined" &&
    exception.mechanism?.synthetic === true
  );
};
